// src/pages/public/Schedule.tsx
// Event schedule / agenda page — pulls from /api/public/event

import { useEffect, useState } from "react";
import { Link } from "react-router-dom";

interface ScheduleItem { time: string; label: string; description: string; icon: string; }
interface Venue { name: string; address: string; mapUrl: string; pavilion: string; parkingNote: string; }
interface EventDoc {
  title: string; tagline: string; date: string; timeStart: string; timeEnd: string;
  venue: Venue; schedule: ScheduleItem[]; status: string;
  showSchedule?: boolean;
  showMap?: boolean;
}

function formatDate(iso: string): string {
  const d = new Date(iso + "T12:00:00");
  return d.toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric", year: "numeric" });
}

export default function Schedule() {
  const [event, setEvent] = useState<EventDoc | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetch("/api/public/event?year=2026")
      .then(r => { if (!r.ok) throw new Error("Event not found"); return r.json(); })
      .then(data => { setEvent(data); setLoading(false); })
      .catch(e => { setError(e.message); setLoading(false); });
  }, []);

  // Page disabled by admin
  if (event && event.showSchedule === false) {
    return (
      <section className="section">
        <div className="container disabled-page">
          <span className="eyebrow">— Coming Soon</span>
          <h1>The schedule isn't published yet</h1>
          <p>Check back closer to the event for the full agenda.</p>
          <Link to="/" className="btn-back">Back to home</Link>
          <style>{`
            .disabled-page { max-width: 640px; padding: 4rem 0; }
            .disabled-page h1 { font-family: var(--font-display); font-weight: 700; text-transform: uppercase; margin: 0.5rem 0 1rem; }
            .disabled-page p { color: var(--steel); margin-bottom: 2rem; font-size: 1.05rem; }
            .eyebrow { font-family: var(--font-display); font-size: 0.75rem; font-weight: 500; letter-spacing: 0.35em; text-transform: uppercase; color: var(--red); }
            .btn-back {
              display: inline-block; font-family: var(--font-display); font-weight: 700;
              font-size: 0.85rem; letter-spacing: 0.1em; text-transform: uppercase;
              color: var(--white); background: var(--red); padding: 0.85rem 2rem;
              text-decoration: none;
            }
          `}</style>
        </div>
      </section>
    );
  }

  return (
    <>
      <style>{`
        .schedule-page { min-height: 100vh; background: var(--bg); }
        .schedule-hero {
          background: var(--black); color: var(--white);
          padding: 5rem 4rem 4rem; position: relative; overflow: hidden;
        }
        .schedule-hero::before {
          content: ''; position: absolute; inset: 0;
          background: repeating-linear-gradient(-45deg, transparent, transparent 12px, rgba(255,255,255,0.02) 12px, rgba(255,255,255,0.02) 24px);
          pointer-events: none;
        }
        .hero-inner { max-width: 1100px; margin: 0 auto; position: relative; }
        .eyebrow {
          font-family: var(--font-display); font-size: 0.72rem; font-weight: 500;
          letter-spacing: 0.35em; text-transform: uppercase; color: var(--red);
          margin-bottom: 0.75rem;
        }
        .schedule-hero h1 {
          font-family: var(--font-display); font-size: clamp(2.4rem, 5vw, 3.6rem);
          font-weight: 700; text-transform: uppercase; line-height: 1; margin: 0;
        }
        .hero-meta {
          display: flex; gap: 2.5rem; flex-wrap: wrap; margin-top: 2rem;
          font-family: var(--font-body); font-size: 0.95rem; color: rgba(255,255,255,0.7);
        }
        .hero-meta a { color: var(--red); text-decoration: none; }
        .hero-meta a:hover { text-decoration: underline; }
        .schedule-body {
          max-width: 1100px; margin: 0 auto; padding: 4rem;
          display: grid; grid-template-columns: 1fr 320px; gap: 4rem;
        }
        @media (max-width: 900px) {
          .schedule-body { grid-template-columns: 1fr; padding: 3rem 1.5rem; }
          .schedule-hero { padding: 3.5rem 1.5rem 3rem; }
        }
        .timeline { list-style: none; margin: 0; padding: 0; position: relative; }
        .timeline::before { content: ""; position: absolute; left: 90px; top: 0; bottom: 0; width: 2px; background: var(--border); }
        .timeline-item { display: grid; grid-template-columns: 90px 16px 1fr; gap: 1.25rem; align-items: flex-start; padding: 1.5rem 0; position: relative; }
        .tl-time { font-family: var(--font-display); font-size: 0.85rem; font-weight: 700; letter-spacing: 0.05em; color: var(--red); padding-top: 0.25rem; text-align: right; }
        .tl-dot { width: 14px; height: 14px; border: 2px solid var(--red); background: var(--white); border-radius: 50%; margin-top: 0.4rem; margin-left: 1px; box-shadow: 0 0 0 4px var(--bg); position: relative; z-index: 1; }
        .tl-content { padding-bottom: 0.5rem; }
        .tl-label { font-family: var(--font-display); font-size: 1.15rem; font-weight: 600; text-transform: uppercase; letter-spacing: 0.04em; color: var(--black); margin: 0 0 0.5rem; display: flex; align-items: center; gap: 0.6rem; }
        .tl-icon { font-size: 1.4rem; }
        .tl-desc { font-family: var(--font-body); font-size: 0.95rem; color: var(--steel); margin: 0; line-height: 1.55; }
        .sidebar { display: flex; flex-direction: column; gap: 1.5rem; }
        .info-card { background: var(--white); border: 1px solid var(--border); padding: 1.5rem; }
        .card-label { font-family: var(--font-display); font-size: 0.65rem; font-weight: 500; letter-spacing: 0.25em; text-transform: uppercase; color: var(--red); margin: 0 0 0.5rem; }
        .card-title { font-family: var(--font-display); font-size: 1.1rem; font-weight: 700; text-transform: uppercase; color: var(--black); margin: 0 0 0.6rem; line-height: 1.2; }
        .card-text { font-family: var(--font-body); font-size: 0.9rem; color: var(--steel); line-height: 1.5; margin: 0 0 0.5rem; }
        .card-link {
          display: inline-block; font-family: var(--font-display); font-size: 0.78rem;
          font-weight: 600; text-transform: uppercase; letter-spacing: 0.05em;
          color: var(--white); background: var(--red); padding: 0.55rem 1.1rem;
          text-decoration: none; margin-top: 0.5rem; transition: background 0.15s;
        }
        .card-link:hover { background: var(--red-dark); }
        .state-box { padding: 4rem 2rem; text-align: center; font-family: var(--font-body); color: var(--muted); }
        .state-box h2 { font-family: var(--font-display); font-size: 1.4rem; font-weight: 600; text-transform: uppercase; color: var(--mid); margin-bottom: 0.5rem; }
      `}</style>

      <div className="schedule-page">
        <div className="schedule-hero">
          <div className="hero-inner">
            <p className="eyebrow">— Event Schedule</p>
            {loading || !event ? (
              <h1>WICnic 2026</h1>
            ) : (
              <>
                <h1>{event.title}</h1>
                <div className="hero-meta">
                  <span>📅 <strong>{formatDate(event.date)}</strong></span>
                  <span>🕐 <strong>{event.timeStart} – {event.timeEnd}</strong></span>
                  <span>
                    📍 <strong>{event.venue.name}</strong>
                    {" · "}<a href={event.venue.mapUrl} target="_blank" rel="noreferrer">Map</a>
                  </span>
                </div>
              </>
            )}
          </div>
        </div>

        {loading && <div className="state-box"><h2>Loading schedule…</h2></div>}
        {error && (
          <div className="state-box">
            <h2>Schedule unavailable</h2>
            <p>Check back soon for the full agenda.</p>
          </div>
        )}

        {!loading && !error && event && (
          <div className="schedule-body">
            <div>
              <p className="eyebrow" style={{ marginBottom: "1.75rem" }}>— Agenda</p>
              <ul className="timeline">
                {event.schedule.map((item, i) => (
                  <li key={i} className="timeline-item">
                    <span className="tl-time">{item.time}</span>
                    <span className="tl-dot" />
                    <div className="tl-content">
                      <h3 className="tl-label"><span className="tl-icon">{item.icon}</span>{item.label}</h3>
                      <p className="tl-desc">{item.description}</p>
                    </div>
                  </li>
                ))}
              </ul>
            </div>

            <aside className="sidebar">
              <div className="info-card">
                <p className="card-label">Location</p>
                <h4 className="card-title">{event.venue.name}</h4>
                <p className="card-text">{event.venue.pavilion}</p>
                <p className="card-text">{event.venue.address}</p>
                <p className="card-text" style={{ fontSize: "0.82rem", color: "var(--muted)" }}>
                  {event.venue.parkingNote}
                </p>
                <a href={event.venue.mapUrl} target="_blank" rel="noreferrer" className="card-link">
                  Open in Google Maps →
                </a>
                {event.showMap && (
                  <Link to="/maps" className="card-link" style={{ background: "var(--black)", marginLeft: "0.5rem" }}>
                    Venue &amp; Parking Maps →
                  </Link>
                )}
              </div>

              <div className="info-card">
                <p className="card-label">Date & Time</p>
                <h4 className="card-title">{formatDate(event.date)}</h4>
                <p className="card-text">{event.timeStart} – {event.timeEnd}</p>
              </div>

              {event.status === "rsvp-open" && (
                <div className="info-card" style={{ background: "var(--red)", border: "none" }}>
                  <p className="card-label" style={{ color: "rgba(255,255,255,0.7)" }}>RSVPs Are Open</p>
                  <h4 className="card-title" style={{ color: "var(--white)" }}>Coming?</h4>
                  <p className="card-text" style={{ color: "rgba(255,255,255,0.85)" }}>
                    Let us know so we have enough food.
                  </p>
                  <a href="/rsvp" className="card-link" style={{ background: "var(--white)", color: "var(--red)" }}>
                    RSVP Now →
                  </a>
                </div>
              )}
            </aside>
          </div>
        )}

      </div>
    </>
  );
}
